import React, { useEffect, useRef } from 'react';
import { useUI } from '../context/UIContext';
import './Hero.css';

/**
 * Hero component
 *
 * Props:
 *   badge        — optional eyebrow label above h1 (e.g. "New · Spring 2026")
 *   title        — main heading (supports JSX for <span className="highlight"> accents)
 *   subtitle     — supporting paragraph text
 *   primaryCta   — { label, onClick } for primary button
 *   secondaryCta — { label, onClick } for secondary button
 *   trustSignal  — optional small line below buttons (e.g. "Trusted by 10,000+ customers")
 *   bgGradient   — boolean: enables the gradient-bg variant
 *   image        — image src URL
 *   imageAlt     — descriptive alt text for the image (required when image is set)
 */
const Hero = ({
  badge,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  trustSignal,
  bgGradient = false,
  image,
  imageAlt = '',
}) => {
  const { openPopup } = useUI();
  const textRef  = useRef(null);
  const imageRef = useRef(null);

  // Trigger entrance animation on mount
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      textRef.current?.classList.add('revealed');
      imageRef.current?.classList.add('revealed');
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Use props if provided, otherwise use high-performance defaults
  const pCta = {
    label: primaryCta?.label || 'Get a Quote',
    onClick: primaryCta?.onClick || openPopup
  };

  const sCta = {
    label: secondaryCta?.label || 'Contact Support',
    onClick: secondaryCta?.onClick || (() => window.location.href='/contact')
  };

  return (
    <header className={`hero${bgGradient ? ' gradient-bg' : ''}`}>

      {/* Ambient background shapes — aria-hidden so screen readers skip them */}
      <span className="shape shape-1" aria-hidden="true" />
      <span className="shape shape-2" aria-hidden="true" />
      <span className="shape shape-3" aria-hidden="true" />

      <div className="container">
        <div className="hero-grid">

          {/* ── Text column ── */}
          <div className="hero-text" ref={textRef}>

            {badge && (
              <span className="hero-badge" aria-label={`Announcement: ${badge}`}>
                {badge}
              </span>
            )}

            <h1>{title}</h1>

            {subtitle && <p>{subtitle}</p>}

            <div className="hero-btns" role="group" aria-label="Primary actions">
              <button
                className="btn btn-primary"
                onClick={pCta.onClick}
                type="button"
              >
                {pCta.label}
              </button>
              <button
                className="btn btn-secondary"
                onClick={sCta.onClick}
                type="button"
              >
                {sCta.label}
              </button>
            </div>

            {trustSignal && (
              <p className="hero-trust" aria-label={trustSignal}>
                <span className="hero-trust-dot" aria-hidden="true">✦</span>
                {trustSignal}
              </p>
            )}
          </div>

          {/* ── Image column ── */}
          {image && (
            <div className="hero-image" ref={imageRef}>
              <img
                src={image}
                alt={imageAlt}
                width={480}
                loading="eager"       /* above-the-fold: eager, not lazy */
                decoding="async"
              />
            </div>
          )}

        </div>
      </div>
    </header>
  );
};

export default Hero;